import { useMemo } from "react";

import { GenericTooltip } from "@components/tooltip-container";
import { SecondaryButton } from "@components/v2/buttons";
import {
  INHERIT_BUDGET_MIX_KPI,
  useMMMBudgetMixKpiId,
} from "@features/mmm/hooks/mmm-url-state";
import { type BudgetMixData } from "@north-beam/nb-common";
import { ExploreBudgetsTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import {
  getMMMEventSelectionData,
  MMMTab,
} from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { useClientKpis } from "@pages/mmm/hooks/use-client-kpis";

import { TogglablePills } from "../../shared/generic/toggleable-pills";
import TableChartToggle from "../../table-chart-toggle";
import { BudgetMixCardMode } from "../budget-mix-card/card";
import { VisibleMixesStrategyDropdown } from "../visible-mixes-strategy-dropdown";

type Props = {
  budgetMixes: BudgetMixData;
  cardMode: BudgetMixCardMode;
  setCardMode: (mode: BudgetMixCardMode) => void;
  openAddSpendDataModal: () => void;
};

export const MixesControls = ({
  budgetMixes,
  cardMode,
  setCardMode,
  openAddSpendDataModal,
}: Props) => {
  const { kpis } = useClientKpis();
  const trackMMMEvent = useTrackMMMEvent(MMMTab.EXPLORE_BUDGETS);
  const { selectedBudgetMixKpiId, setSelectedBudgetMixKpiId } =
    useMMMBudgetMixKpiId();

  const kpiPills = useMemo(
    () => [
      {
        displayValue: "Inherit from setting",
        value: INHERIT_BUDGET_MIX_KPI,
      },
      ...kpis.map((kpi) => ({
        displayValue: kpi.friendly_name,
        value: String(kpi.id),
      })),
    ],
    [kpis],
  );
  return (
    <>
      <div className="flex items-center my-2">
        <div className="text-xl font-semibold">Compare Your Media Mixes</div>
        <GenericTooltip
          className="h-7"
          hideArrow
          placement="right"
          content="MMM+ creates a range of budgets and recommends the best ones based on your settings"
        />
      </div>
      <div className="w-full flex justify-between gap-2 items-center">
        <div className="flex flex-col justify-start items-start gap-1">
          <span className="text-xs">KPI</span>
          <TogglablePills
            pillWidthClassname="w-fit"
            maxWidth="max-w-[172px]"
            pills={kpiPills}
            selectedValue={selectedBudgetMixKpiId}
            onClick={(val) => {
              trackMMMEvent({
                component:
                  ExploreBudgetsTrackedComponent.BUDGET_MIX_CARD_KPI_SELECTOR,
                action: "onSelect",
                actionInfo: "Change selected budget mix KPI",
                data: getMMMEventSelectionData(
                  kpiPills.find((pill) => pill.value === val)?.displayValue,
                ),
              });
              setSelectedBudgetMixKpiId(String(val));
            }}
            showCount={3}
          />
        </div>
        <div className="flex items-end gap-4">
          <SecondaryButton
            className="text-v2-primary font-semibold h-8.5"
            onClick={openAddSpendDataModal}
          >
            <i className="fa-regular fa-sharp fa-plus mr-1.5" />
            CREATE YOUR OWN BUDGET MIX
          </SecondaryButton>
          <VisibleMixesStrategyDropdown />
          <div className="flex flex-col justify-start items-start gap-1">
            <span className="uppercase text-xs">View</span>
            <TableChartToggle
              onToggle={(value) => {
                trackMMMEvent({
                  component:
                    ExploreBudgetsTrackedComponent.BUDGET_MIX_TABLE_CHART_TOGGLE,
                  action: "onSelect",
                  actionInfo: "Toggle budget mix display mode",
                  data: getMMMEventSelectionData(
                    value === BudgetMixCardMode.CHART ? "Chart" : "Table",
                  ),
                });

                setCardMode(value);
              }}
              toggleState={cardMode}
            />
          </div>
        </div>
      </div>
    </>
  );
};
